import { escapeAttr, isValidAnchor, } from "@emkit/builder-core";
import { buildColumnStyle, buildSectionStyle, styleObjectToCssString } from "./style.js";
/**
 * Build CSS-variable declarations + font-family for the page wrapper from
 * the theme. Returns a string fragment ready to slot into a `style="..."`
 * attribute (or the empty string if no theme is configured).
 *
 * Pattern: each color token becomes `--emk-color-{name}: ...`. Widgets
 * referencing tokens by string (`"$brand"`) get them resolved via
 * `resolveColorToken` at render time, so the CSS vars are mostly there
 * for user-supplied custom CSS that wants to read them.
 */
function buildThemeStyle(theme) {
    if (!theme)
        return "";
    const parts = [];
    const colors = theme.colors ?? {};
    for (const [name, value] of Object.entries(colors)) {
        if (value)
            parts.push(`--emk-color-${name}:${value}`);
    }
    if (typeof theme.baseRadius === "number") {
        parts.push(`--emk-radius:${theme.baseRadius}px`);
    }
    if (theme.fontFamily) {
        parts.push(`font-family:${theme.fontFamily}`);
        parts.push(`--emk-font-family:${theme.fontFamily}`);
    }
    if (theme.headingFontFamily) {
        parts.push(`--emk-heading-font-family:${theme.headingFontFamily}`);
    }
    return parts.join(";");
}
/** CSS rule to apply heading-font-family token to descendant headings. */
const THEME_HEADING_CSS = ".emk-page :where(h1,h2,h3,h4,h5,h6){font-family:var(--emk-heading-font-family,inherit)}";
/**
 * CSS injected once per page when any wrapper uses `hideOn`. The classes
 * are toggled at server render based on the visibility rules; the actual
 * media-query gating happens client-side via these rules.
 *
 * Breakpoints chosen to match the editor's `BreakpointSwitcher` and the
 * widely-used Tailwind defaults: mobile ≤640, tablet 641-1024, desktop 1025+.
 */
const VISIBILITY_CSS = "@media (max-width:640px){.emk-hide-mobile{display:none!important}}" +
    "@media (min-width:641px) and (max-width:1024px){.emk-hide-tablet{display:none!important}}" +
    "@media (min-width:1025px){.emk-hide-desktop{display:none!important}}";
/** Build the visibility class list for a wrapper. Empty string when none. */
function visibilityClasses(custom) {
    const h = custom.hideOn;
    if (!h)
        return "";
    const parts = [];
    if (h.mobile)
        parts.push("emk-hide-mobile");
    if (h.tablet)
        parts.push("emk-hide-tablet");
    if (h.desktop)
        parts.push("emk-hide-desktop");
    return parts.join(" ");
}
/** Compose `class` attribute value: base + visibility flags + user className. */
function composeClass(base, custom) {
    const vis = visibilityClasses(custom);
    return [base, vis, custom.className].filter(Boolean).join(" ");
}
/** Compose `style` attribute value: base style + user-supplied style. */
function composeStyle(base, custom) {
    if (!custom.style)
        return base;
    if (!base)
        return custom.style;
    return base.endsWith(";") ? `${base}${custom.style}` : `${base};${custom.style}`;
}
/** Render the optional `id="..."` attribute when the anchor is valid. */
function anchorAttr(custom) {
    return isValidAnchor(custom.anchor) ? ` id="${escapeAttr(custom.anchor)}"` : "";
}
const renderSectionVideoLayer = (bg) => {
    if (!bg || bg.kind !== "video")
        return "";
    const poster = bg.poster ? ` poster="${escapeAttr(bg.poster)}"` : "";
    return `<video class="emk-section-bg-video" autoplay muted loop playsinline${poster} style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;pointer-events:none"><source src="${escapeAttr(bg.url)}"></video>`;
};
export function renderWidget(widget, registry, ctx) {
    const cls = composeClass("emk-widget", widget);
    const styleAttr = widget.style ? ` style="${escapeAttr(widget.style)}"` : "";
    return `<div class="${escapeAttr(cls)}"${anchorAttr(widget)} data-widget-id="${escapeAttr(widget.id)}" data-widget-type="${escapeAttr(widget.type)}"${styleAttr}>${registry.render(widget, ctx)}</div>`;
}
export function renderColumn(column, registry, ctx) {
    const baseStyle = styleObjectToCssString(buildColumnStyle(column, ctx.breakpoint));
    const cls = composeClass("emk-column", column);
    const style = composeStyle(baseStyle, column);
    const inner = column.widgets.map((w) => renderWidget(w, registry, ctx)).join("");
    return `<div class="${escapeAttr(cls)}"${anchorAttr(column)} data-column-id="${escapeAttr(column.id)}" style="${escapeAttr(style)}">${inner}</div>`;
}
export function renderSection(section, registry, ctx) {
    const styles = buildSectionStyle(section, ctx.breakpoint);
    const outerStyle = styleObjectToCssString(styles.outer);
    const innerStyle = styleObjectToCssString(styles.inner);
    const cls = composeClass("emk-section", section);
    const style = composeStyle(outerStyle, section);
    const videoBg = renderSectionVideoLayer(section.background);
    const columnsHtml = section.columns.map((c) => renderColumn(c, registry, ctx)).join("");
    return `<section class="${escapeAttr(cls)}"${anchorAttr(section)} data-section-id="${escapeAttr(section.id)}" style="${escapeAttr(style)}">${videoBg}<div class="emk-section-inner" style="${escapeAttr(innerStyle)}">${columnsHtml}</div></section>`;
}
export function renderPage(page, options) {
    const breakpoint = options.breakpoint ?? "desktop";
    const ctx = {
        breakpoint,
        resolveAssetUrl: options.resolveAssetUrl,
        theme: page.theme,
    };
    const body = page.sections.map((s) => renderSection(s, options.registry, ctx)).join("");
    const themeStyle = buildThemeStyle(page.theme);
    const themeStyleAttr = themeStyle ? ` style="${escapeAttr(themeStyle)}"` : "";
    if (!options.fullDocument) {
        // Fragment mode: prepend a tiny <style> block so visibility classes work
        // when this fragment is mounted into a host layout that doesn't ship the
        // CSS itself. Always emitted (~200 bytes) so consumers don't have to
        // detect whether any descendant uses hideOn.
        return `<style>${VISIBILITY_CSS}${page.theme?.headingFontFamily ? THEME_HEADING_CSS : ""}</style><div class="emk-page" data-page-id="${escapeAttr(page.id)}"${themeStyleAttr}>${body}</div>`;
    }
    const desc = page.meta?.description ?? "";
    const ogTitle = page.meta?.og?.title ?? page.title;
    const ogDesc = page.meta?.og?.description ?? desc;
    const ogImage = page.meta?.og?.image ?? "";
    const robots = page.meta?.robots ?? "index,follow";
    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeAttr(page.title)}</title>
    ${desc ? `<meta name="description" content="${escapeAttr(desc)}">` : ""}
    <meta name="robots" content="${escapeAttr(robots)}">
    <meta property="og:title" content="${escapeAttr(ogTitle)}">
    ${ogDesc ? `<meta property="og:description" content="${escapeAttr(ogDesc)}">` : ""}
    ${ogImage ? `<meta property="og:image" content="${escapeAttr(ogImage)}">` : ""}
    <style>
      :root { color-scheme: light; }
      body { margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; }
      * { box-sizing: border-box; }
      .emk-page { overflow: hidden; }
      ${VISIBILITY_CSS}
      ${page.theme?.headingFontFamily ? THEME_HEADING_CSS : ""}
    </style>
  </head>
  <body>
    <div class="emk-page" data-page-id="${escapeAttr(page.id)}"${themeStyleAttr}>
      ${body}
    </div>
  </body>
</html>`;
}
//# sourceMappingURL=render-html.js.map